// "LAST UPDATED", AT THE TOP OF CONSTRUCTION TRACKING (owner, 2026-10-17: a
// clear tracker next to the sources bar - when the model data was last
// updated, and when the tracking quantities were last rebuilt. Plain and
// visible, not in a tooltip).
//
// Two facts, said separately, because they move separately: a new model
// version lands at one time and its quantities are rebuilt minutes or hours
// later, and until the rebuild finishes the numbers on this page are the
// old model's or none at all. So a rebuild in progress says so, with how far
// it has got, rather than showing the previous time as if it were current.
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { getQuantityStatus } from "api/tracking/trackingElementsApi";

const LABEL = { fontSize: 10.5, fontWeight: 600, color: "#8D8D8D", letterSpacing: ".06em" };
const when = (d) => (d ? moment(d).format("MMM D, YYYY h:mm A") : "");

export function modelLine(model) {
  if (!model || !model.commitId) return "No published model yet";
  const at = when(model.promotedAt || model.committedAt);
  const bits = [at ? `Model updated ${at}` : "Model date not recorded"];
  if (model.exportVersion) bits.push(`version ${model.exportVersion}`);
  bits.push(`commit ${String(model.commitId).slice(0, 8)}`);
  if (model.exportVersion && !model.live) bits.push("not the live version");
  return bits.join(" · ");
}

export function quantitiesLine(q) {
  const state = q && q.state;
  if (!state || state === "none") return "Tracking quantities not built yet";
  if (state === "waiting") return `Tracking quantities waiting${q.reason ? ` - ${q.reason}` : ""}`;
  if (state === "queued") return "Tracking quantities rebuild queued - numbers below are not this model's yet";
  if (state === "running") {
    const of = q.files ? ` (${q.filesDone || 0} of ${q.files} files)` : "";
    return `Tracking quantities rebuilding${of} - numbers below are not this model's yet`;
  }
  if (state === "failed" || state === "error" || q.error) return `Tracking quantities rebuild failed${q.error ? `: ${q.error}` : ""}`;
  return q.finishedAt ? `Tracking quantities rebuilt ${when(q.finishedAt)}` : "Tracking quantities built";
}

export default function TrackingFreshness({ projectId, refreshKey }) {
  const [status, setStatus] = useState(null);
  const [failed, setFailed] = useState(false);
  const read = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await getQuantityStatus(projectId);
      setStatus(r || null);
      setFailed(false);
    } catch (e) {
      setFailed(true);
    }
  }, [projectId]);
  useEffect(() => { setStatus(null); read(); }, [read, refreshKey]);
  // While a rebuild is under way, keep the line honest without a reload.
  const busy = status && status.quantities && ["queued", "running"].includes(status.quantities.state);
  useEffect(() => {
    if (!projectId) return undefined;
    const t = setInterval(read, busy ? 30000 : 300000);
    return () => clearInterval(t);
  }, [projectId, busy, read]);
  if (!projectId) return null;
  const q = status && status.quantities;
  const stale = q && ["queued", "running", "failed", "error", "waiting"].includes(q.state);
  return (
    <div data-testid="tracking-freshness" style={{
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      background: stale ? "#FFF8E6" : "#fff", border: `1px solid ${stale ? "#F2D58A" : "#EFEFEF"}`,
      borderRadius: 6, padding: "7px 14px", marginBottom: 12, fontSize: 12.5, color: "#3A3A3A",
    }}>
      <span style={LABEL}>LAST UPDATED</span>
      {failed && !status ? <span>Could not read when the model was last updated</span> : null}
      {status ? <span data-testid="freshness-model">{modelLine(status.model)}</span> : null}
      {status ? <span data-testid="freshness-quantities" style={{ color: stale ? "#8A5A00" : "#3A3A3A" }}>{quantitiesLine(q)}</span> : null}
      {!status && !failed ? <span style={{ color: "#8D8D8D" }}>Reading…</span> : null}
    </div>
  );
}
